import type { AuthSession } from './AuthContext';
import { buildApiUrl } from '../common/api/apiBaseUrl';

interface ExchangeResponse {
  access_token: string;
  user: {
    displayName: string;
    authType: 'INTERNAL' | 'EXTERNAL';
    email?: string;
    isAdmin?: boolean;
    persisted?: AuthSession['persisted'];
  };
}

// Normalized API response from /api/rfi/auth/external/exchange.
// The backend maps the raw upstream REF payload (EeS or UHCeS) into this app-facing shape.
interface ExternalExchangeResponse {
  route: 'EXTERNAL';
  portal?: string;
  access_token?: string;
  user?: {
    displayName?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    isAdmin?: boolean;
    persisted?: AuthSession['persisted'];
    uuid?: string;
    ohid?: string;
    clientType?: string;
    policyIds?: string[];
  };
}

export async function exchangeAuthCode(code: string): Promise<AuthSession> {
  const response = await fetch(buildApiUrl('/api/rfi/auth/internal/exchange'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => {return null})) as { message?: string } | null;
    throw new Error(body?.message ?? `Auth request failed: ${response.status}`);
  }

  const data = (await response.json()) as ExchangeResponse;

  return {
    token: data.access_token,
    displayName: data.user.displayName,
    authType: data.user.authType,
    email: data.user.email,
    isAdmin: data.user.isAdmin,
    persisted: data.user.persisted,
  };
}

export async function exchangeExternalRef(ref: string): Promise<AuthSession> {
  const response = await fetch(buildApiUrl('/api/rfi/auth/external/exchange'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ref }),
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => {return null})) as { message?: string } | null;
    throw new Error(body?.message ?? `Auth request failed: ${response.status}`);
  }

  const data = (await response.json()) as ExternalExchangeResponse;
  if (data.route !== 'EXTERNAL' || !data.access_token || !data.user) {
    throw new Error('External auth response is missing required session data');
  }

  const firstName = data.user.firstName?.trim() ?? '';
  const lastName = data.user.lastName?.trim() ?? '';
  const displayName = data.user.displayName?.trim() || `${firstName} ${lastName}`.trim() || 'External User';

  return {
    token: data.access_token,
    displayName,
    authType: 'EXTERNAL',
    email: data.user.email,
    isAdmin: data.user.isAdmin,
    persisted: data.user.persisted,
    externalContext: {
      portal: data.portal,
      uuid: data.user.uuid,
      ohid: data.user.ohid,
      clientType: data.user.clientType,
      policyIds: data.user.policyIds ?? [],
    },
  };
}

export function redirectToAzureLogin() {
  if (typeof window !== 'undefined') {
    window.location.assign(buildApiUrl('/api/rfi/auth/internal/login'));
  }
}

export function requestLogout() {
  if (typeof window === 'undefined') return;

  fetch(buildApiUrl('/api/rfi/auth/logout'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  }).catch(() => {
    // Best-effort logout; local session clear still applies.
  });
}
